'use client';

import { useMemo } from 'react';
import { Flex, Box } from '@skin-seoul/design-system';
import { createReviewStatsMap, getPriceProductBasicInfo, getProductType, useProductService, useRegionCurrency, ProductStockStatus } from '@skin-seoul/service';
import { useReviewService } from '@skin-seoul/service';
import FlashSaleProductCard from '@/components/products/ProductCard/FlashSaleProductCard';
import { Carousel } from '@/components/ui/Carousel';
import YearEndCampaignProductSwiperSkeleton from './YearEndCampaignProductSwiper.skeleton';
const PRODUCT_PAGE_NUMBER = 1;
const PRODUCT_LIMIT = 12;
type YearEndCampaignProductSwiperProps = {
  collectionName: string;
};
export const YearEndCampaignProductSwiper = ({
  collectionName
}: YearEndCampaignProductSwiperProps) => {
  const {
    currency
  } = useRegionCurrency();
  const {
    useGetProductsCollectionsQuery
  } = useProductService();
  const {
    useGetReviewStatistics
  } = useReviewService();
  const {
    data,
    isLoading
  } = useGetProductsCollectionsQuery({
    collectionName,
    page: PRODUCT_PAGE_NUMBER,
    limit: PRODUCT_LIMIT,
    stockStatus: ProductStockStatus.instock,
    currency
  });
  const products = useMemo(() => {
    return data?.products ?? [];
  }, [data]);
  const {
    data: reviewStats
  } = useGetReviewStatistics({
    productIds: products.map(product => product.productId).join(',')
  });
  const reviewStatsMap = useMemo(() => createReviewStatsMap(reviewStats), [reviewStats]);
  if (isLoading) {
    return <YearEndCampaignProductSwiperSkeleton />;
  }
  if (products.length === 0) {
    return <Flex width="100%" justifyContent="center" alignItems="center" padding="40px 0">
        <Box color="neutral.60" textStyle="body2">
          Campaign products coming soon
        </Box>
      </Flex>;
  }
  return <Box position="relative" width="100%" data-sentry-element="Box" data-sentry-component="YearEndCampaignProductSwiper" data-sentry-source-file="YearEndCampaignProductSwiper.tsx">
      <Carousel opts={{
      loop: true,
      align: 'start',
      skipSnaps: false
    }} w="100%" data-sentry-element="Carousel" data-sentry-source-file="YearEndCampaignProductSwiper.tsx">
        <Carousel.Content data-sentry-element="Carousel.Content" data-sentry-source-file="YearEndCampaignProductSwiper.tsx">
          {products.map(product => {
          const lowestPriceProductInfo = getPriceProductBasicInfo(product);
          const productType = getProductType(product);
          const productReviewStats = reviewStatsMap[product.productId];
          return <Carousel.Item key={product.productId} flexBasis={{
            base: '50%',
            tablet: '33.333%',
            desktop: '20%'
          }} px="12px">
                <FlashSaleProductCard href={product.productLink} name={product.name} brand={product.brand} productImage={product.productImageUrls[0]} sku={product.sku} currency={product.currency} originalPrice={lowestPriceProductInfo.originalPrice} salePrice={lowestPriceProductInfo.salePrice} rating={productReviewStats?.averageRate} reviewCount={productReviewStats?.totalReviews} productType={productType} productId={product.productId.toString()} category={product.category} />
              </Carousel.Item>;
        })}
        </Carousel.Content>
        <Carousel.Previous display={{
        base: 'none',
        desktop: 'flex'
      }} left={{
        base: '-12px',
        desktop: '-24px'
      }} top="40%" backgroundColor="white" borderWidth="1px" borderColor="neutral.400" width="48px" height="48px" borderRadius="50%" backdropFilter="blur(16px)" variant="ghost" css={{
        '& svg': {
          width: '18px',
          height: '18px'
        }
      }} data-sentry-element="Carousel.Previous" data-sentry-source-file="YearEndCampaignProductSwiper.tsx" />
        <Carousel.Next display={{
        base: 'none',
        desktop: 'flex'
      }} right={{
        base: '-12px',
        desktop: '-24px'
      }} top="40%" backgroundColor="white" borderWidth="1px" borderColor="neutral.400" width="48px" height="48px" borderRadius="50%" backdropFilter="blur(16px)" variant="ghost" css={{
        '& svg': {
          width: '18px',
          height: '18px'
        }
      }} data-sentry-element="Carousel.Next" data-sentry-source-file="YearEndCampaignProductSwiper.tsx" />
      </Carousel>
    </Box>;
};